import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, call } from '../client';
import type { paths } from '../schema';

/* Step 11: inbox (Step 17b: with opportunities), reply settings, simulated replies, the bell. */

type Query<P extends keyof paths> = NonNullable<
  paths[P] extends { get: { parameters: { query?: infer Q } } } ? Q : never
>;
const clean = <T extends object>(q: T): T =>
  Object.fromEntries(Object.entries(q).filter(([, v]) => v !== undefined && v !== '')) as T;

export type InboxQuery = {
  [K in keyof Query<'/v1/workspaces/{workspaceId}/inbox'>]?:
    Query<'/v1/workspaces/{workspaceId}/inbox'>[K] | undefined;
};

export function useInbox(workspaceId: string | null, query: InboxQuery) {
  return useQuery({
    queryKey: ['inbox', workspaceId ?? 'none', query],
    queryFn: () =>
      call(
        api.GET('/v1/workspaces/{workspaceId}/inbox', {
          params: {
            path: { workspaceId: workspaceId ?? '' },
            query: clean(query) as Query<'/v1/workspaces/{workspaceId}/inbox'>,
          },
        }),
      ),
    enabled: workspaceId !== null,
    placeholderData: keepPreviousData,
    refetchInterval: 30_000,
  });
}
export type InboxRow = NonNullable<ReturnType<typeof useInbox>['data']>['emails'][number];

export function useInboxEmail(id: string | null) {
  return useQuery({
    queryKey: ['inbox-email', id ?? 'none'],
    queryFn: () =>
      call(api.GET('/v1/inbox/{emailId}', { params: { path: { emailId: id ?? '' } } })),
    enabled: id !== null,
  });
}
export type InboxDetail = NonNullable<ReturnType<typeof useInboxEmail>['data']>;

function useRefreshReplies() {
  const qc = useQueryClient();
  return () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: ['inbox'] }),
      qc.invalidateQueries({ queryKey: ['inbox-email'] }),
      qc.invalidateQueries({ queryKey: ['notifications'] }),
    ]);
}

export function useMarkRead() {
  const refresh = useRefreshReplies();
  return useMutation({
    mutationFn: (body: { ids: string[]; read: boolean }) =>
      call(api.POST('/v1/inbox/read', { body })),
    onSuccess: () => refresh(),
  });
}

export function useReplyFromInbox(id: string) {
  const refresh = useRefreshReplies();
  return useMutation({
    mutationFn: (body: { text: string; idempotencyKey: string }) =>
      call(api.POST('/v1/inbox/{emailId}/reply', { params: { path: { emailId: id } }, body })),
    onSuccess: () => refresh(),
  });
}

export function useReplySettings(workspaceId: string | null) {
  return useQuery({
    queryKey: ['reply-settings', workspaceId ?? 'none'],
    queryFn: () =>
      call(
        api.GET('/v1/workspaces/{workspaceId}/reply-settings', {
          params: { path: { workspaceId: workspaceId ?? '' } },
        }),
      ),
    enabled: workspaceId !== null,
  });
}

export function useSaveReplySettings(workspaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      aiSortReplies?: boolean;
      inboxReplyEnabled?: boolean;
      opportunityEmail?: string | null;
    }) =>
      call(
        api.PUT('/v1/workspaces/{workspaceId}/reply-settings', {
          params: { path: { workspaceId } },
          body,
        }),
      ),
    onSuccess: (r) => qc.setQueryData(['reply-settings', workspaceId], r),
  });
}

export function useSimulateReply(campaignId: string) {
  const refresh = useRefreshReplies();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      campaignLeadId: string;
      kind: 'reply' | 'out_of_office' | 'bounce';
      text?: string | null;
    }) =>
      call(
        api.POST('/v1/campaigns/{campaignId}/simulate-reply', {
          params: { path: { campaignId } },
          body,
        }),
      ),
    onSuccess: () =>
      Promise.all([
        refresh(),
        qc.invalidateQueries({ queryKey: ['campaign', campaignId] }),
        qc.invalidateQueries({ queryKey: ['campaign-leads', campaignId] }),
      ]),
  });
}

/** The bell: polled every 30 seconds. */
export function useNotifications() {
  return useQuery({
    queryKey: ['notifications'],
    queryFn: () => call(api.GET('/v1/notifications')),
    refetchInterval: 30_000,
  });
}

export function useReadNotifications() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids?: string[]) =>
      call(api.POST('/v1/notifications/read', { body: ids ? { ids } : {} })),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  });
}
